/**
 * Single row inside `DashboardSwitcher.tsx`'s saved-dashboard list — split out
 * to keep the parent under the repo's ~200-line guideline. Select button on
 * the left; Rename/Duplicate/Delete reveal on row hover (or keyboard focus)
 * on the right. The parent owns all the actual mutations (reused from
 * `explorer/dashboardExplorerActions.ts`) — this component is presentational
 * plus the hover-reveal chrome.
 */
import { Check, Copy, Pencil, Trash2 } from 'lucide-react';
import type { Dashboard } from '../types/dashboardTypes';

type DashboardSwitcherRowProps = {
  dashboard: Dashboard;
  isActive: boolean;
  /** True at the free-tier cap (non-Pro only) — Duplicate disables with a WHY tooltip. */
  duplicateBlocked: boolean;
  duplicateBlockedReason: string;
  onSelect: () => void;
  onRename: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
};

export function DashboardSwitcherRow({
  dashboard,
  isActive,
  duplicateBlocked,
  duplicateBlockedReason,
  onSelect,
  onRename,
  onDuplicate,
  onDelete,
}: DashboardSwitcherRowProps) {
  return (
    <div
      role="option"
      aria-selected={isActive}
      className="group/row flex items-center rounded-md hover:bg-muted"
    >
      <button
        className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm"
        onClick={onSelect}
        data-testid={`dashboard-switcher-item-${dashboard.id}`}
      >
        <span className="flex-1 truncate" title={dashboard.name}>{dashboard.name}</span>
        <span className="shrink-0 text-xs text-muted-foreground">
          {dashboard.tiles.length} {dashboard.tiles.length === 1 ? 'tile' : 'tiles'}
        </span>
        {isActive && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
      </button>
      <div className="flex shrink-0 items-center gap-0.5 pr-1 opacity-0 group-hover/row:opacity-100 group-focus-within/row:opacity-100 has-[:focus-visible]:opacity-100">
        <button
          type="button"
          title="Rename"
          className="rounded p-1 text-muted-foreground hover:bg-background hover:text-foreground"
          onClick={onRename}
          data-testid={`dashboard-switcher-item-${dashboard.id}-rename`}
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        {/* Disabled `<button>`s don't reliably surface `title` on hover — the
            WHY reason lives on this wrapping `<span>` instead (same
            convention as the "New" button below and the explorer context
            menu's disabled "Duplicate" item). */}
        <span title={duplicateBlocked ? duplicateBlockedReason : undefined}>
          <button
            type="button"
            title={duplicateBlocked ? undefined : 'Duplicate'}
            disabled={duplicateBlocked}
            className="rounded p-1 text-muted-foreground hover:bg-background hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
            onClick={onDuplicate}
            data-testid={`dashboard-switcher-item-${dashboard.id}-duplicate`}
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
        </span>
        <button
          type="button"
          title="Delete"
          className="rounded p-1 text-muted-foreground hover:bg-background hover:text-destructive"
          onClick={onDelete}
          data-testid={`dashboard-switcher-item-${dashboard.id}-delete`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
